import { fetchWithNetworkLogging } from "@Front/Api/helpers/requestHelpers";
import { createBaseNotary, type IBaseNotary } from "../BaseNotaryFactory";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { userFacingMessageFromApiClientError } from "@Front/Utils/userFacingMessageFromApiClientError";
import { ContentType } from "@Front/Api/ContentType";
import type {
	ISearchOfficesResponse,
	IOfficeMembersResponse,
	SearchOfficesPagination,
	SearchOfficesContext,
} from "./SearchOffices";

interface SearchOfficesInstance {
	searchOffices(
		query: string,
		context: SearchOfficesContext,
		pagination?: SearchOfficesPagination,
	): Promise<ISearchOfficesResponse>;
	getOfficeMembers(
		officeIdNot: string,
		context: SearchOfficesContext,
		officeName?: string,
	): Promise<IOfficeMembersResponse>;
}

let instance: SearchOfficesInstance | null = null;
let baseNotaryInstance: IBaseNotary | null = null;

function normalizeSearchOfficesError(err: unknown, query: string): ISearchOfficesResponse {
	LoggerService.error("❌ Erreur recherche offices", { query, error: err });
	if (
		err &&
		typeof err === "object" &&
		"offices" in err &&
		"annuaireConfigured" in err
	) {
		return err as ISearchOfficesResponse;
	}
	return {
		offices: [],
		annuaireConfigured: true,
		hasMore: false,
		message: userFacingMessageFromApiClientError(err, "Impossible de récupérer les offices. Réessayez plus tard."),
	};
}

function applyPaginationToSearchOfficesUrl(url: URL, pagination?: SearchOfficesPagination): void {
	if (pagination?.limit != null && Number.isFinite(pagination.limit)) {
		url.searchParams.set("limit", String(Math.max(1, Math.floor(pagination.limit))));
	}
	if (pagination?.offset != null && Number.isFinite(pagination.offset) && pagination.offset > 0) {
		url.searchParams.set("offset", String(Math.max(0, Math.floor(pagination.offset))));
	}
}

function applyFolderUidToUrl(url: URL, folderUid: string): void {
	url.searchParams.set("folderUid", folderUid);
}

async function fetchSearchOfficesHttp(url: string, baseNotary: IBaseNotary, query: string): Promise<ISearchOfficesResponse> {
	try {
		const headers = baseNotary.buildHeaders(ContentType.JSON);
		const response = await fetchWithNetworkLogging(url, {
			method: "GET",
			headers,
			credentials: "include",
		});
		if (response.status === 503) {
			return (await response.json()) as ISearchOfficesResponse;
		}
		if (!response.ok) {
			const text = await response.text();
			throw new Error(text || `HTTP ${response.status}`);
		}
		return (await response.json()) as ISearchOfficesResponse;
	} catch (err) {
		return normalizeSearchOfficesError(err, query);
	}
}

async function doSearchOffices(
	query: string,
	context: SearchOfficesContext,
	baseNotary: IBaseNotary,
	pagination?: SearchOfficesPagination,
): Promise<ISearchOfficesResponse> {
	if (!query || query.length < 2 || !context.folderUid?.trim()) {
		return { offices: [], annuaireConfigured: true, hasMore: false };
	}
	const url = new URL(baseNotary.namespaceUrl.concat("/search/offices"));
	url.searchParams.set("q", query);
	applyFolderUidToUrl(url, context.folderUid.trim());
	applyPaginationToSearchOfficesUrl(url, pagination);
	return fetchSearchOfficesHttp(url.toString(), baseNotary, query);
}

async function doGetOfficeMembers(
	officeIdNot: string,
	context: SearchOfficesContext,
	officeName: string | undefined,
	baseNotary: IBaseNotary,
): Promise<IOfficeMembersResponse> {
	if (!context.folderUid?.trim()) {
		throw new Error("folderUid is required for annuaire office members");
	}
	const url = new URL(baseNotary.namespaceUrl.concat("/search/offices/", encodeURIComponent(officeIdNot), "/members"));
	applyFolderUidToUrl(url, context.folderUid.trim());
	if (officeName) url.searchParams.set("officeName", officeName);
	const response = await fetchWithNetworkLogging(url.toString(), {
		method: "GET",
		headers: baseNotary.buildHeaders(ContentType.JSON),
		credentials: "include",
	});
	if (!response.ok) {
		const text = await response.text();
		throw new Error(text || `HTTP ${response.status}`);
	}
	return (await response.json()) as IOfficeMembersResponse;
}

function createSearchOffices(): SearchOfficesInstance {
	if (!baseNotaryInstance) {
		baseNotaryInstance = createBaseNotary();
	}
	const baseNotary = baseNotaryInstance;
	return {
		searchOffices: (query: string, context: SearchOfficesContext, pagination?: SearchOfficesPagination) =>
			doSearchOffices(query, context, baseNotary, pagination),
		getOfficeMembers: (officeIdNot: string, context: SearchOfficesContext, officeName?: string) =>
			doGetOfficeMembers(officeIdNot, context, officeName, baseNotary),
	};
}

export function getSearchOfficesInstance(): SearchOfficesInstance {
	if (!instance) {
		instance = createSearchOffices();
	}
	return instance;
}
